import type { ApplicationInsights, IExceptionTelemetry } from '@microsoft/applicationinsights-web';
import { User } from 'oidc-client-ts';
import config from '../config';

let appInsights: ApplicationInsights | undefined;
let authenticatedUserProperties: Record<string, string> = {};

export type TelemetryProperties = Record<string, string | number | boolean | undefined | null>;

function getException(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (error && typeof error === 'object') {
    return new Error(JSON.stringify(error));
  }

  return new Error(String(error));
}

function stringifyProperties(properties?: TelemetryProperties): Record<string, string> | undefined {
  if (!properties) {
    return undefined;
  }

  const result: Record<string, string> = {};
  Object.keys(properties).forEach((key) => {
    const value = properties[key];
    if (value !== undefined && value !== null) {
      result[key] = String(value);
    }
  });

  return result;
}

function getRouteContext(): TelemetryProperties {
  if (typeof window === 'undefined') {
    return {};
  }

  const pathname = window.location.pathname;
  const match = pathname.match(
    /^\/company\/(?<companyId>\d+)(?:\/account\/(?<accountId>\d+))?(?:\/sim\/submission\/(?<ticketNumber>[^/]+))?/
  );

  let routeTemplate = pathname;
  if (match) {
    routeTemplate = '/company/:companyId';
    if (match.groups?.accountId) {
      routeTemplate += '/account/:accountId';
    }
    if (pathname.includes('/sim/submission/')) {
      routeTemplate += '/sim/submission/:ticketNumber';
    } else if (pathname.endsWith('/sim')) {
      routeTemplate += '/sim';
    }
  }

  return {
    routeTemplate,
    pathname,
    companyId: match?.groups?.companyId,
    accountId: match?.groups?.accountId,
    ticketNumber: match?.groups?.ticketNumber,
    userAgent: window.navigator.userAgent,
    language: window.navigator.language,
    appVersion: config.APP_VERSION,
  };
}

export function getTelemetryContext(properties?: TelemetryProperties): Record<string, string> | undefined {
  return stringifyProperties({
    ...getRouteContext(),
    ...properties,
  });
}

async function loadApplicationInsights() {
  if (!config.APPINSIGHTS_CONNECTION_STRING || config.isLocal()) {
    return;
  }

  const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');
  appInsights = new ApplicationInsights({
    config: {
      connectionString: config.APPINSIGHTS_CONNECTION_STRING,
      enableAutoRouteTracking: true,
      enableUnhandledPromiseRejectionTracking: true,
    },
  });

  appInsights.addTelemetryInitializer((envelope) => {
    const baseData = envelope.baseData as { properties?: Record<string, string> } | undefined;
    if (!baseData || Object.keys(authenticatedUserProperties).length === 0) {
      return;
    }

    baseData.properties = {
      ...baseData.properties,
      ...authenticatedUserProperties,
    };
  });

  appInsights.loadAppInsights();
  if (authenticatedUserProperties.userId) {
    appInsights.setAuthenticatedUserContext(authenticatedUserProperties.userId);
  }
  appInsights.trackPageView();
}

export function initializeApplicationInsights() {
  if (!config.APPINSIGHTS_CONNECTION_STRING || config.isLocal()) {
    return;
  }

  const initialize = () => window.setTimeout(() => void loadApplicationInsights(), 3000);
  if (document.readyState === 'complete') {
    initialize();
  } else {
    window.addEventListener('load', initialize, { once: true });
  }
}

export function setAuthenticatedUser(user: User) {
  const id = user.profile.sub;
  if (!id) {
    return;
  }

  authenticatedUserProperties = {
    userId: id,
    userName: user.profile.name || '',
    isSubportalUser: String(user.profile.subportal_user === true),
  };
  appInsights?.setAuthenticatedUserContext(id);
}

export function clearAuthenticatedUser() {
  authenticatedUserProperties = {};
  appInsights?.clearAuthenticatedUserContext();
}

export function trackException(error: unknown, properties?: IExceptionTelemetry['properties']) {
  if (!appInsights) {
    return;
  }

  appInsights.trackException({
    exception: getException(error),
    properties: getTelemetryContext(properties as TelemetryProperties),
  });
}

export function sanitizeUrlPath(url?: string): string | undefined {
  if (!url) {
    return undefined;
  }

  try {
    if (typeof document === 'undefined') {
      return url.split('?')[0];
    }

    const parser = document.createElement('a');
    parser.href = url;
    return parser.pathname || url.split('?')[0];
  } catch {
    return url.split('?')[0];
  }
}
